/* eslint-disable no-console */
import { datadogLogs, Site } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import internalUserGuidHashes from './TrackingDefinitions';

export interface TrackingEvent {
    eventCategory: string;
    eventAction: string;
    eventValue?: any;
}

export class TrackingService {
    private userGuid: string | undefined;

    private userEmail: string | null | undefined;

    private isInternalStoneXUser = false;

    private static datadogClientInitialized = false;

    private static datadogAppContext: { service: string; env: string; version: string } | undefined;

    private static isConsoleDebugEnabled(): boolean {
        try {
            return localStorage.getItem('enableConsoleDebug') === 'true';
        } catch {
            return false;
        }
    }

    private static safeGetInternalContext(): Record<string, unknown> {
        try {
            return (datadogRum.getInternalContext() as Record<string, unknown>) ?? {};
        } catch {
            return {};
        }
    }

    private static safeGetUserContext(): Record<string, unknown> {
        try {
            return (datadogRum.getUser() as Record<string, unknown>) ?? {};
        } catch {
            return {};
        }
    }

    private static async hashGuid(guid: string): Promise<string> {
        const encoded = new TextEncoder().encode(guid.toLowerCase());
        const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
        return Array.from(new Uint8Array(hashBuffer))
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
    }

    private static resolveUserType(email: string): 'stonex' | 'xenots' | 'yopmail' | 'external' {
        const lower = email.toLowerCase();
        if (lower.includes('stonex')) {
            return 'stonex';
        }
        if (lower.includes('xenots')) {
            return 'xenots';
        }
        if (lower.includes('yopmail')) {
            return 'yopmail';
        }
        return 'external';
    }

    private static maskEmail(email: string): string {
        const atIndex = email.indexOf('@');
        if (atIndex > 2) {
            return `${email.substring(0, 2)}***${email.substring(atIndex)}`;
        }
        return email;
    }

    private static toDisplayValue(value: unknown): string | null {
        let displayValue: string | null = null;

        try {
            if (value === undefined || value === null) {
                displayValue = null;
            } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                displayValue = String(value);
            } else {
                displayValue = JSON.stringify(value);
            }
        } catch {
            displayValue = String(value);
        }

        if (displayValue && displayValue.length > 200) {
            return `${displayValue.slice(0, 197)}...`;
        }

        return displayValue;
    }

    private static toDatadogValue(value: unknown): unknown {
        if (value === undefined) {
            return undefined;
        }

        if (value === null) {
            return null;
        }

        const t = typeof value;

        if (t === 'string' || t === 'number' || t === 'boolean') {
            return value;
        }

        if (t === 'bigint') {
            return (value as bigint).toString();
        }

        if (t === 'symbol' || t === 'function') {
            return TrackingService.toDisplayValue(value);
        }

        if (value instanceof Date) {
            return value.toISOString();
        }

        if (value instanceof Error) {
            return {
                name: value.name,
                message: value.message,
                stack: value.stack,
            };
        }

        try {
            if (Array.isArray(value)) {
                return value.slice(0, 50).map((x) => TrackingService.toDatadogValue(x));
            }

            if (t === 'object') {
                const obj = value as Record<string, unknown>;
                const out: Record<string, unknown> = {};

                let count = 0;
                let truncated = false;

                Object.keys(obj).some((key) => {
                    if (count >= 50) {
                        truncated = true;
                        return true;
                    }

                    const v = obj[key];
                    if (v === undefined) {
                        return false;
                    }

                    out[key] = TrackingService.toDatadogValue(v);
                    count += 1;
                    return false;
                });

                if (truncated) {
                    out.truncated = true;
                }

                return out;
            }
        } catch {
            return TrackingService.toDisplayValue(value);
        }

        return TrackingService.toDisplayValue(value);
    }

    private static normalizeRoutePath(rawPath: string): string {
        if (!rawPath) {
            return '/';
        }

        const trimmed = rawPath.replace(/^\/+/, '');

        if (!trimmed) {
            return '/';
        }

        return trimmed;
    }

    private static buildViewNameFromLocation(): string {
        try {
            const { pathname, search } = window.location;

            const params = new URLSearchParams(search);
            const routeParam = params.get('route');

            if (routeParam) {
                const normalized = TrackingService.normalizeRoutePath(routeParam);
                return normalized;
            }

            const normalizedPath = TrackingService.normalizeRoutePath(pathname);
            return normalizedPath;
        } catch {
            return '/';
        }
    }

    private static setViewNameFromCurrentUrl(): void {
        if (!TrackingService.datadogClientInitialized) {
            return;
        }

        try {
            if (typeof window === 'undefined') {
                return;
            }

            const viewName = TrackingService.buildViewNameFromLocation();
            datadogRum.setViewName(viewName);
        } catch {
            console.warn('Failed to set view name in Datadog RUM');
        }
    }

    private applyUserToDatadogIfAvailable(): void {
        if (!TrackingService.datadogClientInitialized) {
            return;
        }

        if (!this.userGuid && !this.userEmail) {
            return;
        }

        try {
            const maskedEmail = this.userEmail ? TrackingService.maskEmail(this.userEmail) : undefined;
            const userType = this.userEmail ? TrackingService.resolveUserType(this.userEmail) : undefined;
            datadogRum.setUser({
                ...(this.userGuid ? { id: this.userGuid } : {}),
                ...(maskedEmail ? { email: maskedEmail } : {}),
                ...(userType ? { userType } : {}),
                isInternalStoneXUser: this.isInternalStoneXUser,
            });

            datadogLogs.setUser({
                ...(this.userGuid ? { id: this.userGuid } : {}),
                ...(maskedEmail ? { email: maskedEmail } : {}),
                ...(userType ? { userType } : {}),
                isInternalStoneXUser: this.isInternalStoneXUser,
            } as any);
        } catch {
            console.warn('Failed to set user in Datadog RUM or Logs');
        }
    }

    setUserGuid = (userGuid: string) => {
        this.userGuid = userGuid;
        TrackingService.hashGuid(userGuid)
            .then((hash) => {
                this.isInternalStoneXUser = internalUserGuidHashes.has(hash);
                this.applyUserToDatadogIfAvailable();
            })
            .catch(() => {
                this.isInternalStoneXUser = false;
                this.applyUserToDatadogIfAvailable();
            });
    };

    setUserEmail = (userEmail: string | null) => {
        this.userEmail = userEmail;
        this.applyUserToDatadogIfAvailable();
    };

    public static initializeDatadog(onInitialized?: () => void): boolean {
        console.log('Attempting to initialize Datadog RUM and Logs');

        if (TrackingService.datadogClientInitialized) {
            console.warn('Datadog client already initialized');
            return true;
        }

        const clientToken = process.env.REACT_APP_DATADGOG_CLIENT_TOKEN;
        const applicationId = process.env.REACT_APP_DATADGOG_APPLICATION_ID;

        if (!clientToken || !applicationId) {
            console.warn('Datadog client token or application ID is missing');
            return false;
        }

        const site = ((process.env.REACT_APP_DATADGOG_SITE as Site) || 'datadoghq.com') as Site;
        const service = process.env.REACT_APP_DATADGOG_SERVICE || 'demeter';
        const env = process.env.REACT_APP_DATADGOG_ENV || 'dev';
        const version = process.env.REACT_APP_DATADGOG_VERSION || '1.0.0';

        TrackingService.datadogAppContext = { service, env, version };

        let rumInitialized = false;
        let logsInitialized = false;

        try {
            datadogRum.init({
                applicationId,
                clientToken,
                site,
                service,
                env,
                version,
                sessionSampleRate: 100,
                sessionReplaySampleRate: 20,
                trackResources: true,
                trackUserInteractions: true,
                trackLongTasks: true,
                defaultPrivacyLevel: 'mask-user-input',
                plugins: [reactPlugin({ router: false })],
            });

            datadogRum.startSessionReplayRecording();
            rumInitialized = true;
        } catch {
            console.warn('Failed to initialize Datadog RUM');
        }

        try {
            datadogLogs.init({
                clientToken,
                site,
                service,
                env,
                version,
                forwardErrorsToLogs: true,
                sessionSampleRate: 100,
            });

            logsInitialized = true;
        } catch {
            console.warn('Failed to initialize Datadog Logs');
        }

        TrackingService.datadogClientInitialized = rumInitialized || logsInitialized;

        if (TrackingService.datadogClientInitialized) {
            try {
                TrackingService.setViewNameFromCurrentUrl();
                onInitialized?.();
            } catch {
                console.warn('Failed to set initial view name in Datadog RUM');
            }
        }

        return TrackingService.datadogClientInitialized;
    }

    track(e: TrackingEvent): void {
        if (!e) {
            return;
        }

        const displayValue = TrackingService.toDisplayValue(e.eventValue);
        const datadogValue = TrackingService.toDatadogValue(e.eventValue);
        const url = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '';

        if (TrackingService.isConsoleDebugEnabled()) {
            const badgeStyle = 'color: #fff; background: #1e88e5; font-weight:700; padding:2px 8px; border-radius:6px;';
            const categoryStyle = 'color:#1e88e5; font-weight:700; padding-left:6px;';
            const keyStyle = 'color:#1565c0; font-weight:600;';
            const valStyle = 'color:#0d47a1; font-weight:600;';
            const metaStyle = 'color:#37474f;';

            const fmt = `%cTRACK%c %s
%caction:%c %s
%cvalue:%c %s
%curl:%c %s`;

            console.info(
                fmt,
                badgeStyle,
                categoryStyle,
                e.eventCategory,
                keyStyle,
                valStyle,
                e.eventAction,
                keyStyle,
                valStyle,
                displayValue ?? 'null',
                keyStyle,
                metaStyle,
                typeof window !== 'undefined' ? window.location.pathname : '',
            );
        }

        if (!TrackingService.datadogClientInitialized) {
            console.warn('Datadog client not initialized. Call TrackingService.initializeDatadog() first.');
            return;
        }

        TrackingService.setViewNameFromCurrentUrl();
        this.applyUserToDatadogIfAvailable();

        const internalContext = TrackingService.safeGetInternalContext();
        const userContext = TrackingService.safeGetUserContext();
        const app = TrackingService.datadogAppContext;

        const context = {
            ...userContext,
            ...internalContext,
            timestamp: new Date().toISOString(),
            url,
            ...(app ? { app } : {}),
            ...(this.userGuid || this.userEmail
                ? {
                      user: {
                          id: this.userGuid,
                          email: this.userEmail ? TrackingService.maskEmail(this.userEmail) : undefined,
                          userType: this.userEmail ? TrackingService.resolveUserType(this.userEmail) : undefined,
                          isInternalStoneXUser: this.isInternalStoneXUser,
                      },
                  }
                : {}),
            event: { category: e.eventCategory, action: e.eventAction, value: datadogValue },
        };

        try {
            datadogRum.addAction(e.eventAction, context);
        } catch {
            console.warn('Failed to track event in Datadog RUM');
        }

        try {
            datadogLogs.logger.info(`User action: ${e.eventAction}`, context);
        } catch {
            console.warn('Failed to log event in Datadog Logs');
        }
    }
}

const trackingService = new TrackingService();

export default trackingService;
